/**
 * Enrollments Management Page
 * Admin page for managing class enrollments with full CRUD
 */

import React, { useState, useEffect, useCallback } from "react";
import { formatDate } from "../../utils/format";
import { usePermissions } from "../../hooks/usePermissions";
import {
  Plus,
  Edit,
  Trash2,
  XCircle,
  CheckCircle,
  Eye,
  X,
  Calendar,
  Clock,
  MapPin,
  Users,
  DollarSign,
  Tag,
  User,
  Loader2,
  Mail,
  ArrowRight,
} from "lucide-react";
import DataTable from "../../components/admin/DataTable";
import FilterBar from "../../components/admin/FilterBar";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import EnrollmentFormModal from "../../components/admin/EnrollmentFormModal";
import EnrollmentReviewPanel from "../../components/admin/EnrollmentReviewPanel";
import TransferModal from "../../components/admin/TransferModal";
import enrollmentsService from "../../api/services/enrollments.service";
import classesService from "../../api/services/classes.service";
import programsService from "../../api/services/programs.service";
import schoolsService from "../../api/services/schools.service";
import toast from "react-hot-toast";
import Header from "../../components/Header";
import { classHoursLabel } from "../../utils/classDayTimes";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "waitlisted", label: "Waitlisted" },
  // Paused was missing entirely, so an owner had no way to list paused
  // memberships — one was only discovered by searching Stripe by name.
  { value: "paused", label: "Paused" },
];

const STATUS_COLORS = {
  active: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
  waitlisted: "bg-purple-100 text-purple-800",
  paused: "bg-orange-100 text-orange-800",
};

const PAYMENT_COLORS = {
  paid: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  unpaid: "bg-red-100 text-red-800",
  refunded: "bg-gray-200 text-gray-700",
  past_due: "bg-red-100 text-red-800",
};

export default function Enrollments() {
  const { can, role } = usePermissions();
  const canSeeInvoices = can("canViewFinancials");
  // Guardrail overrides are owner-only; the modal hides the reason field for
  // admins and tells them an owner can complete it.
  const isOwner = role === "owner";
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [classes, setClasses] = useState([]);
  const [transferModal, setTransferModal] = useState({
    isOpen: false,
    enrollment: null,
    submitting: false,
  });
  const [programFilter, setProgramFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [programsList, setProgramsList] = useState([]);
  const [schoolsList, setSchoolsList] = useState([]);
  const [stats, setStats] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  const [viewModal, setViewModal] = useState({
    isOpen: false,
    enrollment: null,
    classData: null,
    loadingDetails: false,
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    action: null,
  });

  // Enrollment Review right drawer
  const [reviewPanel, setReviewPanel] = useState({
    open: false,
    loading: false,
    enrollment: null,
    data: null,
  });

  const openReviewPanel = async (enrollment) => {
    setReviewPanel({ open: true, loading: true, enrollment, data: null });
    try {
      const data = await enrollmentsService.getReview(enrollment.id);
      setReviewPanel({ open: true, loading: false, enrollment, data });
    } catch (error) {
      console.error("Failed to load enrollment review:", error);
      toast.error("Failed to load enrollment review");
      setReviewPanel({
        open: false,
        loading: false,
        enrollment: null,
        data: null,
      });
    }
  };

  const closeReviewPanel = () =>
    setReviewPanel({
      open: false,
      loading: false,
      enrollment: null,
      data: null,
    });

  // Invoices per enrollment, loaded lazily when a row is expanded:
  // { [enrollmentId]: { loading, error, items } }
  const [invoicesByEnrollment, setInvoicesByEnrollment] = useState({});

  const loadEnrollmentInvoices = async (row) => {
    if (invoicesByEnrollment[row.id]?.items) return; // already loaded
    setInvoicesByEnrollment((prev) => ({
      ...prev,
      [row.id]: { loading: true, error: null, items: null },
    }));
    try {
      const data = await enrollmentsService.getInvoices(row.id);
      setInvoicesByEnrollment((prev) => ({
        ...prev,
        [row.id]: { loading: false, error: null, items: data.items || [] },
      }));
    } catch (error) {
      console.error("Failed to fetch enrollment invoices:", error);
      setInvoicesByEnrollment((prev) => ({
        ...prev,
        [row.id]: {
          loading: false,
          error: "Failed to load invoices",
          items: null,
        },
      }));
    }
  };

  const INVOICE_STATUS_COLORS = {
    paid: "bg-green-100 text-green-800",
    sent: "bg-yellow-100 text-yellow-800",
    draft: "bg-gray-100 text-gray-700",
    overdue: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-500",
  };

  const renderEnrollmentInvoices = (row) => {
    const state = invoicesByEnrollment[row.id];
    if (!state || state.loading) {
      return (
        <p className="text-sm text-text-muted font-manrope py-2">
          Loading invoices…
        </p>
      );
    }
    if (state.error) {
      return (
        <p className="text-sm text-red-600 font-manrope py-2">{state.error}</p>
      );
    }
    if (!state.items?.length) {
      return (
        <p className="text-sm text-text-muted font-manrope py-2">
          No invoices for this enrollment yet.
        </p>
      );
    }
    return (
      <div className="overflow-x-auto">
        <p className="text-xs font-bold uppercase tracking-wide text-text-muted font-manrope mb-2">
          Invoices ({state.items.length})
        </p>
        <table className="w-full text-sm font-manrope">
          <thead>
            <tr className="text-left text-xs text-text-muted border-b border-border-light">
              <th className="py-2 pr-4">Invoice #</th>
              <th className="py-2 pr-4">Date</th>
              <th className="py-2 pr-4">Description</th>
              <th className="py-2 pr-4">Period</th>
              <th className="py-2 pr-4 text-right">Total</th>
              <th className="py-2 pr-4 text-right">Paid</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {state.items.map((inv) => {
              const status = (inv.status || "").toLowerCase();
              return (
                <tr key={inv.id} className="border-b border-border-light/60">
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {inv.invoice_number}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {formatDate(inv.invoice_date)}
                  </td>
                  <td className="py-2 pr-4 max-w-[280px] truncate">
                    {inv.description || "—"}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {inv.billing_period_start
                      ? `${formatDate(inv.billing_period_start)} – ${formatDate(
                          inv.billing_period_end,
                        )}`
                      : "—"}
                  </td>
                  <td className="py-2 pr-4 text-right whitespace-nowrap">
                    ${Number(inv.total).toFixed(2)}
                  </td>
                  <td className="py-2 pr-4 text-right whitespace-nowrap">
                    ${Number(inv.amount_paid).toFixed(2)}
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        INVOICE_STATUS_COLORS[status] ||
                        "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {inv.status}
                    </span>
                  </td>
                  <td className="py-2 whitespace-nowrap">
                    {inv.pdf_url && (
                      <a
                        href={inv.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#173151] underline text-xs font-semibold hover:text-[#F3BC48]"
                      >
                        PDF
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const [response, progs, schools, statsData] = await Promise.all([
        classesService.getAll({ limit: 500 }),
        programsService.getAll().catch(() => []),
        schoolsService.getAll().catch(() => []),
        enrollmentsService.getStats().catch(() => null),
      ]);
      setClasses(response.items || []);
      setProgramsList(Array.isArray(progs) ? progs : progs?.items || []);
      setSchoolsList(Array.isArray(schools) ? schools : schools?.items || []);
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch classes:", error);
    }
  };

  const fetchEnrollments = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const params = {
        limit: itemsPerPage,
        offset,
      };

      if (statusFilter) params.status = statusFilter;
      if (classFilter) params.class_id = classFilter;
      if (programFilter) params.program_id = programFilter;
      if (schoolFilter) params.school_id = schoolFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const response = await enrollmentsService.getAll(params);
      setEnrollments(response.items || []);
      setTotalItems(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch enrollments:", error);
      toast.error("Failed to load enrollments");
      setEnrollments([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    statusFilter,
    classFilter,
    programFilter,
    schoolFilter,
    searchQuery,
  ]);

  useEffect(() => {
    // Debounce so each keystroke doesn't fire a request
    const t = setTimeout(fetchEnrollments, 300);
    return () => clearTimeout(t);
  }, [fetchEnrollments]);

  // dateFrom/dateTo are applied client-side to the rows already fetched, so
  // they narrow the list exactly like the server-side filters and must reset
  // the page for the same reason.
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    statusFilter,
    classFilter,
    programFilter,
    schoolFilter,
    dateFrom,
    dateTo,
  ]);

  const handleCreateEnrollment = () => {
    setModalMode("create");
    setSelectedEnrollment(null);
    setModalOpen(true);
  };

  const handleEditEnrollment = (enrollment) => {
    setModalMode("edit");
    setSelectedEnrollment(enrollment);
    setModalOpen(true);
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    try {
      await enrollmentsService.delete(enrollmentId);
      toast.success("Enrollment deleted successfully");
      fetchEnrollments();
      setConfirmDialog({ isOpen: false });
    } catch (error) {
      console.error("Failed to delete enrollment:", error);
      toast.error(error.message || "Failed to delete enrollment");
    }
  };

  const handleCancelEnrollment = async (enrollmentId) => {
    try {
      // The staff path: cancels now and stops billing. The parent cancel
      // endpoint only files a request when the class has started.
      await enrollmentsService.cancelAsStaff(
        enrollmentId,
        "Cancelled by admin",
      );
      toast.success("Enrollment cancelled successfully");
      fetchEnrollments();
      setConfirmDialog({ isOpen: false });
    } catch (error) {
      console.error("Failed to cancel enrollment:", error);
      toast.error(error.message || "Failed to cancel enrollment");
    }
  };

  const handleActivateEnrollment = async (enrollmentId) => {
    try {
      await enrollmentsService.activate(enrollmentId);
      toast.success("Enrollment activated successfully");
      fetchEnrollments();
    } catch (error) {
      console.error("Failed to activate enrollment:", error);
      toast.error(error.message || "Failed to activate enrollment");
    }
  };

  const handleSendPaymentLink = async (enrollmentId) => {
    const loadingToast = toast.loading("Creating payment link...");
    try {
      const result = await enrollmentsService.sendPaymentLink(enrollmentId);
      toast.success(
        `Payment link emailed to ${result?.parent_email || "the parent"}`,
        { id: loadingToast },
      );
    } catch (error) {
      console.error("Failed to send payment link:", error);
      toast.error(error.message || "Failed to send payment link", {
        id: loadingToast,
      });
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEnrollment(null);
  };

  const handleModalSuccess = () => {
    setModalOpen(false);
    setSelectedEnrollment(null);
    fetchEnrollments();
  };

  const handleViewEnrollment = async (enrollment) => {
    setViewModal({
      isOpen: true,
      enrollment,
      classData: null,
      loadingDetails: true,
    });
    try {
      const [fullEnrollment, classData] = await Promise.allSettled([
        enrollmentsService.getById(enrollment.id),
        enrollment.class_id
          ? classesService.getById(enrollment.class_id)
          : Promise.resolve(null),
      ]);
      setViewModal((prev) => ({
        ...prev,
        enrollment:
          fullEnrollment.status === "fulfilled"
            ? fullEnrollment.value
            : enrollment,
        classData: classData.status === "fulfilled" ? classData.value : null,
        loadingDetails: false,
      }));
    } catch {
      setViewModal((prev) => ({ ...prev, loadingDetails: false }));
    }
  };

  const closeViewModal = () => {
    setViewModal({
      isOpen: false,
      enrollment: null,
      classData: null,
      loadingDetails: false,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return "$0.00";
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const columns = [
    {
      key: "child_name",
      label: "Child",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-semibold text-[#173151] font-manrope">
            {value || "Unknown"}
          </p>
          <p className="text-xs text-gray-500 font-manrope">
            {row.child_age != null ? `Age ${row.child_age}` : ""}
            {row.child_age != null ? " · " : ""}ID: {row.child_id?.slice(0, 8)}
          </p>
        </div>
      ),
    },
    {
      key: "class_name",
      label: "Class",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-semibold text-[#173151] text-sm font-manrope">
            {value || "Unknown"}
          </p>
          <p className="text-xs text-gray-500 font-manrope">
            {row.program_name || row.school_name || ""}
          </p>
        </div>
      ),
    },
    {
      key: "parent_name",
      label: "Parent / Guardian",
      sortable: true,
      hideOnMobile: true,
      render: (value, row) => (
        <div className="text-sm font-manrope">
          <p className="font-semibold text-[#173151]">{value || "—"}</p>
          {row.parent_email && (
            <p className="text-xs text-gray-500">{row.parent_email}</p>
          )}
          {row.parent_phone && (
            <p className="text-xs text-gray-500">{row.parent_phone}</p>
          )}
        </div>
      ),
    },
    {
      key: "payment_status",
      label: "Payment",
      sortable: true,
      render: (value) =>
        value ? (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
              PAYMENT_COLORS[value] || "bg-gray-100 text-gray-700"
            }`}
          >
            {value.replace("_", " ")}
          </span>
        ) : (
          <span className="text-xs text-gray-400 font-manrope">—</span>
        ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
            STATUS_COLORS[value] || "bg-gray-100 text-gray-800"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "final_price",
      label: "Price",
      sortable: true,
      render: (value, row) => (
        <div className="text-sm font-manrope">
          <p className="font-semibold text-[#173151]">{formatPrice(value)}</p>
          {row.discount_amount > 0 && (
            <p className="text-xs text-green-600">
              -{formatPrice(row.discount_amount)} discount
            </p>
          )}
        </div>
      ),
    },
    {
      key: "selected_custom_fees",
      label: "Fees",
      sortable: true,
      render: (value) => {
        const fees = value || [];
        if (fees.length === 0) {
          return (
            <span className="text-xs text-gray-400 font-manrope">None</span>
          );
        }
        const total = fees.reduce((sum, f) => sum + Number(f.amount || 0), 0);
        return (
          <div className="text-sm font-manrope">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
              {fees.length} fee{fees.length > 1 ? "s" : ""}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">{formatPrice(total)}</p>
          </div>
        );
      },
    },
    {
      key: "enrolled_at",
      label: "Enrolled",
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-600 font-manrope">
          {formatDate(value)}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-500 font-manrope">
          {formatDate(value)}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      type: "actions",
      align: "right",
      actions: (row) => {
        const actions = [
          {
            label: "View",
            icon: Eye,
            onClick: () => openReviewPanel(row),
          },
          {
            label: "Edit",
            icon: Edit,
            onClick: () => handleEditEnrollment(row),
          },
        ];

        // Add activate button for pending enrollments
        if (row.status === "pending") {
          actions.push({
            label: "Activate",
            icon: CheckCircle,
            onClick: () => handleActivateEnrollment(row.id),
          });
          actions.push({
            label: "Email payment link",
            icon: Mail,
            onClick: () => handleSendPaymentLink(row.id),
          });
        }

        // Transfer: only meaningful while the child is still attending.
        if (row.status === "active" || row.status === "pending") {
          actions.push({
            label: "Transfer to another class",
            icon: ArrowRight,
            onClick: () =>
              setTransferModal({
                isOpen: true,
                enrollment: row,
                submitting: false,
              }),
          });
        }

        // Add cancel button for active/pending enrollments
        if (row.status === "active" || row.status === "pending") {
          actions.push({
            label: "Cancel",
            icon: XCircle,
            variant: "warning",
            onClick: () => {
              setConfirmDialog({
                isOpen: true,
                title: "Cancel Enrollment",
                message: `Are you sure you want to cancel the enrollment for "${row.child_name}" in "${row.class_name}"?`,
                action: () => handleCancelEnrollment(row.id),
              });
            },
          });
        }

        // Always add delete button
        actions.push({
          label: "Delete",
          icon: Trash2,
          variant: "destructive",
          onClick: () => {
            setConfirmDialog({
              isOpen: true,
              title: "Delete Enrollment",
              message: `Are you sure you want to permanently delete this enrollment? This action cannot be undone.`,
              action: () => handleDeleteEnrollment(row.id),
            });
          },
        });

        return actions;
      },
    },
  ];

  const handleConfirmTransfer = async ({ targetClassId, overrideReason }) => {
    const { enrollment } = transferModal;
    if (!enrollment || !targetClassId) return;

    setTransferModal((prev) => ({ ...prev, submitting: true }));
    try {
      const result = await enrollmentsService.transfer(
        enrollment.id,
        targetClassId,
        overrideReason,
      );
      const net = parseFloat(result?.net_amount ?? 0);
      const moneyNote =
        net < 0
          ? ` $${Math.abs(net).toFixed(2)} credited.`
          : net > 0
            ? ` $${net.toFixed(2)} ${result.settlement_method === "card" ? "charged" : "invoiced"}.`
            : "";
      toast.success(
        `${result?.child_name || "Student"} moved to ${result?.target_class_name || "the new class"}.${moneyNote}`,
      );
      if (result?.waitlist_promoted_name) {
        toast.success(
          `${result.waitlist_promoted_name} was promoted from the waitlist.`,
        );
      }
      setTransferModal({ isOpen: false, enrollment: null, submitting: false });
      fetchEnrollments();
    } catch (error) {
      console.error("Transfer failed:", error);
      toast.error(error?.message || "Transfer failed");
      setTransferModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const filters = [
    {
      type: "select",
      placeholder: "All Statuses",
      value: statusFilter,
      onChange: setStatusFilter,
      options: STATUS_OPTIONS,
    },
    {
      type: "select",
      placeholder: "All Programs",
      value: programFilter,
      onChange: setProgramFilter,
      options: [
        { value: "", label: "All Programs" },
        ...programsList.map((prg) => ({ value: prg.id, label: prg.name })),
      ],
    },
    {
      type: "select",
      placeholder: "All Classes",
      value: classFilter,
      onChange: setClassFilter,
      options: [
        { value: "", label: "All Classes" },
        ...classes
          .filter((c) => !programFilter || c.program_id === programFilter)
          .map((c) => ({ value: c.id, label: c.name })),
      ],
    },
    {
      type: "select",
      placeholder: "All Locations",
      value: schoolFilter,
      onChange: setSchoolFilter,
      options: [
        { value: "", label: "All Locations" },
        ...schoolsList.map((sc) => ({ value: sc.id, label: sc.name })),
      ],
    },
    {
      type: "daterange",
      startValue: dateFrom,
      endValue: dateTo,
      onStartChange: setDateFrom,
      onEndChange: setDateTo,
    },
  ];

  const hasActiveFilters =
    statusFilter ||
    classFilter ||
    programFilter ||
    schoolFilter ||
    searchQuery ||
    dateFrom ||
    dateTo;
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setClassFilter("");
    setProgramFilter("");
    setSchoolFilter("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  const filteredEnrollments = enrollments.filter((e) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !e.child_name?.toLowerCase().includes(q) &&
        !e.class_name?.toLowerCase().includes(q) &&
        !e.user_name?.toLowerCase().includes(q)
      )
        return false;
    }
    if (dateFrom && new Date(e.created_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(e.created_at) > new Date(dateTo + "T23:59:59"))
      return false;
    return true;
  });

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />

      <div className="max-w-9xl mx-auto px-3 sm:px-4 py-4 flex-1 flex flex-col min-h-0 w-full">
        <div className="shrink-0 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center items-start gap-3 sm:gap-4 justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-[30px] lg:text-[46px] font-bold text-text-primary font-kollektif truncate">
              Enrollments Management
            </h1>
            <p className="text-xs sm:text-sm text-neutral-main font-manrope mt-1 hidden sm:block">
              Create, manage, and track all class enrollments
            </p>
          </div>

          <button
            onClick={handleCreateEnrollment}
            className="flex items-center gap-1.5 sm:gap-2 font-manrope bg-btn-gold text-text-body px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base shrink-0"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">Create </span>Enrollment
          </button>
        </div>

        {stats?.counts && (
          <div className="shrink-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
            {[
              {
                key: "active",
                label: "Active",
                style: "text-green-600 bg-green-50",
              },
              {
                key: "pending",
                label: "Pending Review",
                style: "text-yellow-600 bg-yellow-50",
              },
              {
                key: "waitlisted",
                label: "Waitlist",
                style: "text-purple-600 bg-purple-50",
              },
              {
                key: "cancelled",
                label: "Cancellations",
                style: "text-red-600 bg-red-50",
              },
              {
                key: "paused",
                label: "Paused",
                style: "text-orange-600 bg-orange-50",
              },
            ].map((card) => {
              const count = stats.counts[card.key] || 0;
              const pct = stats.total
                ? Math.round((count / stats.total) * 100)
                : 0;
              return (
                <button
                  key={card.key}
                  type="button"
                  onClick={() => {
                    setStatusFilter(statusFilter === card.key ? "" : card.key);
                    setCurrentPage(1);
                  }}
                  className={`text-left bg-white/60 rounded-2xl p-4 shadow-sm border transition-colors ${
                    statusFilter === card.key
                      ? "border-[#F3BC48]"
                      : "border-white/40 hover:border-[#F3BC48]/50"
                  }`}
                >
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold font-manrope ${card.style}`}
                  >
                    {card.label}
                  </span>
                  <p className="text-2xl font-semibold text-[#173151] font-kollektif mt-1">
                    {count}
                  </p>
                  <p className="text-xs text-gray-500 font-manrope">
                    {pct}% of total
                  </p>
                </button>
              );
            })}
          </div>
        )}

        <div className="shrink-0">
          <FilterBar
            searchValue={searchQuery}
            searchPlaceholder="Search by child or class name..."
            onSearch={setSearchQuery}
            filters={filters}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
          />
        </div>

        <div className="flex-1 min-h-0 flex flex-col pb-2">
          <DataTable
            columns={columns}
            data={filteredEnrollments}
            loading={loading}
            emptyMessage="No enrollments found"
            pagination={true}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            expandable={canSeeInvoices}
            onExpand={loadEnrollmentInvoices}
            renderExpanded={renderEnrollmentInvoices}
          />
        </div>
      </div>

      <EnrollmentFormModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        mode={modalMode}
        initialData={selectedEnrollment}
        onSuccess={handleModalSuccess}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant="danger"
      />

      <TransferModal
        isOpen={transferModal.isOpen}
        enrollment={transferModal.enrollment}
        classes={classes}
        isOwner={isOwner}
        isSubmitting={transferModal.submitting}
        onClose={() =>
          setTransferModal({
            isOpen: false,
            enrollment: null,
            submitting: false,
          })
        }
        onConfirm={handleConfirmTransfer}
      />

      <EnrollmentReviewPanel
        open={reviewPanel.open}
        loading={reviewPanel.loading}
        data={reviewPanel.data}
        onClose={closeReviewPanel}
        onApprove={async () => {
          await handleActivateEnrollment(reviewPanel.enrollment.id);
          closeReviewPanel();
        }}
        onReassign={() => {
          const enr = reviewPanel.enrollment;
          closeReviewPanel();
          handleEditEnrollment(enr);
        }}
        onCancel={() => {
          const enr = reviewPanel.enrollment;
          closeReviewPanel();
          setConfirmDialog({
            isOpen: true,
            title: "Cancel Enrollment",
            message: `Cancel ${enr?.child_name || "this child"}'s enrollment? Membership billing (if any) will be stopped.`,
            action: () => handleCancelEnrollment(enr.id),
          });
        }}
        onSendPaymentLink={() =>
          handleSendPaymentLink(reviewPanel.enrollment.id)
        }
        onSendReminder={async () => {
          const loadingToast = toast.loading("Sending payment reminder...");
          try {
            const res = await enrollmentsService.sendPaymentReminder(
              reviewPanel.enrollment.id,
            );
            toast.success(
              `Reminder sent to ${res?.parent_email || "the parent"}`,
              { id: loadingToast },
            );
          } catch (error) {
            console.error("Failed to send reminder:", error);
            toast.error(error.message || "Failed to send reminder", {
              id: loadingToast,
            });
          }
        }}
      />

      {/* View Enrollment Modal */}
      {viewModal.isOpen &&
        (() => {
          const enr = viewModal.enrollment;
          const cls = viewModal.classData;
          const isLoading = viewModal.loadingDetails;

          const statusLabel = enr?.status || "N/A";
          const statusColor =
            STATUS_COLORS[statusLabel] || "bg-gray-100 text-gray-800";

          const childName =
            enr?.child_name || enr?.child?.first_name
              ? `${enr?.child?.first_name || ""} ${enr?.child?.last_name || ""}`.trim()
              : "Unknown";
          const className = enr?.class_name || cls?.name || "Unknown";
          const schoolName =
            enr?.school_name || cls?.school?.name || cls?.school_name || "";
          const programName = cls?.program?.name || cls?.program_name || "";
          const areaName = cls?.area?.name || cls?.area_name || "";

          const weekdays = enr?.weekdays || cls?.weekdays || [];
          const scheduleStr = classHoursLabel(cls) ||
            (weekdays.length > 0
              ? `${weekdays.map((d) => d.substring(0, 3)).join(", ")} ${cls?.start_time || ""} – ${cls?.end_time || ""}`
              : cls?.schedule?.length > 0
                ? cls.schedule
                    .map(
                      (s) =>
                        `${s.day_of_week?.substring(0, 3)} ${s.start_time}-${s.end_time}`,
                    )
                    .join(", ")
                : "N/A");

          const selectedFees = enr?.selected_custom_fees || [];
          const allClassFees = cls?.custom_fees || [];
          const selectedFeeNames = new Set(selectedFees.map((f) => f.name));
          const hasAnyFees = allClassFees.length > 0 || selectedFees.length > 0;

          return (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4"
              onClick={closeViewModal}
            >
              <div
                className="bg-white rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gray-50">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-base sm:text-lg text-heading-dark font-manrope flex items-center gap-1.5 sm:gap-2">
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-btn-gold shrink-0" />
                      Enrollment Details
                    </h3>
                  </div>
                  <button
                    onClick={closeViewModal}
                    className="p-1.5 sm:p-2 hover:bg-gray-200 rounded-lg transition-colors shrink-0"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-5">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-btn-gold" />
                    </div>
                  ) : (
                    <>
                      {/* Title + Status */}
                      <div className="mb-4">
                        <h2 className="text-lg sm:text-xl font-bold text-heading-dark font-manrope">
                          {className}
                        </h2>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${statusColor}`}
                          >
                            {statusLabel}
                          </span>
                          {programName && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
                              {programName}
                            </span>
                          )}
                          {areaName && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium">
                              {areaName}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* Child */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <User className="w-3.5 h-3.5 text-btn-gold shrink-0" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              Child
                            </span>
                          </div>
                          <p className="text-sm text-text-primary font-manrope font-semibold">
                            {childName}
                          </p>
                          {enr?.child_id && (
                            <p className="text-xs text-text-muted font-mono mt-0.5">
                              ID: {enr.child_id.slice(0, 8)}...
                            </p>
                          )}
                        </div>

                        {/* Schedule */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Calendar className="w-3.5 h-3.5 text-btn-gold shrink-0" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              Schedule
                            </span>
                          </div>
                          <p className="text-sm text-text-primary font-manrope">
                            {scheduleStr}
                          </p>
                        </div>

                        {/* Dates */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Clock className="w-3.5 h-3.5 text-btn-gold shrink-0" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              Class Dates
                            </span>
                          </div>
                          <p className="text-sm text-text-primary font-manrope">
                            {cls?.start_date
                              ? formatDate(cls.start_date)
                              : "N/A"}{" "}
                            — {cls?.end_date ? formatDate(cls.end_date) : "N/A"}
                          </p>
                        </div>

                        {/* Enrolled Date */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <CheckCircle className="w-3.5 h-3.5 text-btn-gold shrink-0" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              Enrolled On
                            </span>
                          </div>
                          <p className="text-sm text-text-primary font-manrope">
                            {formatDate(enr?.enrolled_at || enr?.created_at)}
                          </p>
                        </div>

                        {/* Location */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <MapPin className="w-3.5 h-3.5 text-btn-gold shrink-0" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              Location
                            </span>
                          </div>
                          <p className="text-sm text-text-primary font-manrope">
                            {cls?.location || schoolName || "To be announced"}
                          </p>
                        </div>

                        {/* Capacity */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Users className="w-3.5 h-3.5 text-btn-gold shrink-0" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              Capacity
                            </span>
                          </div>
                          <p className="text-sm text-text-primary font-manrope">
                            <span className="font-semibold">
                              {cls?.current_enrollment || 0}
                            </span>
                            <span className="text-text-muted">
                              {" "}
                              / {cls?.capacity || 0} enrolled
                            </span>
                          </p>
                        </div>

                        {/* Cancellation Info (if cancelled) */}
                        {enr?.status === "cancelled" && (
                          <div className="bg-red-50 rounded-lg p-3 sm:col-span-2">
                            <div className="flex items-center gap-2 mb-1.5">
                              <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                              <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">
                                Cancellation
                              </span>
                            </div>
                            <p className="text-sm text-red-700 font-manrope">
                              {enr.cancelled_at
                                ? `Cancelled on ${formatDate(enr.cancelled_at)}`
                                : "Cancelled"}
                              {enr.cancellation_reason &&
                                ` — ${enr.cancellation_reason}`}
                            </p>
                          </div>
                        )}

                        {/* Waitlist Info (if waitlisted) */}
                        {enr?.status === "waitlisted" && (
                          <div className="bg-purple-50 rounded-lg p-3 sm:col-span-2">
                            <div className="flex items-center gap-2 mb-1.5">
                              <Clock className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                              <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">
                                Waitlist
                              </span>
                            </div>
                            <p className="text-sm text-purple-700 font-manrope">
                              Priority:{" "}
                              <span className="font-semibold capitalize">
                                {enr.waitlist_priority || "Regular"}
                              </span>
                              {enr.auto_promote && " • Auto-promote enabled"}
                              {enr.claim_window_expires_at &&
                                ` • Claim expires: ${formatDate(enr.claim_window_expires_at)}`}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Pricing Section */}
                      <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-3 py-2.5 border-b border-gray-200">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-btn-gold shrink-0" />
                            <span className="text-sm font-semibold text-heading-dark font-manrope">
                              Pricing
                            </span>
                          </div>
                        </div>
                        <div className="p-3 sm:p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-text-muted font-manrope">
                              Base Price
                            </span>
                            <span className="text-sm font-semibold text-heading-dark font-manrope">
                              {formatPrice(enr?.base_price)}
                            </span>
                          </div>
                          {Number(enr?.discount_amount) > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-green-600 font-manrope">
                                Discount
                              </span>
                              <span className="text-sm font-semibold text-green-600 font-manrope">
                                -{formatPrice(enr.discount_amount)}
                              </span>
                            </div>
                          )}
                          {selectedFees.length > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-muted font-manrope">
                                Custom Fees ({selectedFees.length})
                              </span>
                              <span className="text-sm font-semibold text-heading-dark font-manrope">
                                {formatPrice(
                                  selectedFees.reduce(
                                    (sum, f) => sum + Number(f.amount || 0),
                                    0,
                                  ),
                                )}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-2 border-t border-dashed border-gray-200">
                            <span className="text-sm font-semibold text-text-primary font-manrope">
                              Final Price
                            </span>
                            <span className="text-lg font-bold text-heading-dark font-manrope">
                              {formatPrice(enr?.final_price)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Custom Fees Breakdown - Paid vs Not Paid */}
                      {hasAnyFees && (
                        <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-2.5 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-btn-gold shrink-0" />
                              <span className="text-sm font-semibold text-heading-dark font-manrope">
                                Custom Fees
                              </span>
                              {selectedFees.length > 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">
                                  {selectedFees.length} paid
                                </span>
                              )}
                              {allClassFees.filter(
                                (f) => !selectedFeeNames.has(f.name),
                              ).length > 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-semibold">
                                  {
                                    allClassFees.filter(
                                      (f) => !selectedFeeNames.has(f.name),
                                    ).length
                                  }{" "}
                                  skipped
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="p-3 sm:p-4 space-y-2">
                            {/* Show all class fees with paid/not-paid status */}
                            {allClassFees.length > 0
                              ? allClassFees.map((fee, idx) => {
                                  const isPaid = selectedFeeNames.has(fee.name);
                                  return (
                                    <div
                                      key={idx}
                                      className={`flex items-start justify-between rounded-lg px-3 py-2.5 ${isPaid ? "bg-green-50" : "bg-gray-50"}`}
                                    >
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                          {isPaid ? (
                                            <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                                          ) : (
                                            <XCircle className="w-4 h-4 text-gray-400 shrink-0" />
                                          )}
                                          <p
                                            className={`text-sm font-medium font-manrope truncate ${isPaid ? "text-text-primary" : "text-gray-400"}`}
                                          >
                                            {fee.name || "Unnamed Fee"}
                                          </p>
                                          <span
                                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${
                                              fee.is_optional
                                                ? "bg-blue-50 text-blue-600"
                                                : "bg-red-50 text-red-600"
                                            }`}
                                          >
                                            {fee.is_optional
                                              ? "Optional"
                                              : "Required"}
                                          </span>
                                        </div>
                                        {fee.description && (
                                          <p className="text-xs text-text-muted font-manrope mt-0.5 ml-6">
                                            {fee.description}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-end shrink-0 ml-3">
                                        <span
                                          className={`text-sm font-bold font-manrope ${isPaid ? "text-heading-dark" : "text-gray-400 line-through"}`}
                                        >
                                          {formatPrice(fee.amount)}
                                        </span>
                                        <span
                                          className={`text-[10px] font-semibold mt-0.5 ${isPaid ? "text-green-600" : "text-gray-400"}`}
                                        >
                                          {isPaid ? "Paid" : "Not Paid"}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })
                              : /* Fallback: if class data not loaded, show selected fees only */
                                selectedFees.map((fee, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-start justify-between bg-green-50 rounded-lg px-3 py-2.5"
                                  >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                                      <p className="text-sm font-medium text-text-primary font-manrope truncate">
                                        {fee.name || "Fee"}
                                      </p>
                                      <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${
                                          fee.is_optional
                                            ? "bg-blue-50 text-blue-600"
                                            : "bg-red-50 text-red-600"
                                        }`}
                                      >
                                        {fee.is_optional
                                          ? "Optional"
                                          : "Required"}
                                      </span>
                                    </div>
                                    <div className="flex flex-col items-end shrink-0 ml-3">
                                      <span className="text-sm font-bold text-heading-dark font-manrope">
                                        {formatPrice(fee.amount)}
                                      </span>
                                      <span className="text-[10px] font-semibold text-green-600 mt-0.5">
                                        Paid
                                      </span>
                                    </div>
                                  </div>
                                ))}
                          </div>
                        </div>
                      )}

                      {/* Subscription Info (if applicable) */}
                      {enr?.stripe_subscription_id && (
                        <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-2.5 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-btn-gold shrink-0" />
                              <span className="text-sm font-semibold text-heading-dark font-manrope">
                                Subscription
                              </span>
                            </div>
                          </div>
                          <div className="p-3 sm:p-4 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-muted font-manrope">
                                Status
                              </span>
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${
                                  enr.subscription_status === "active"
                                    ? "bg-green-100 text-green-800"
                                    : enr.subscription_status === "past_due"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {enr.subscription_status || "N/A"}
                              </span>
                            </div>
                            {enr.current_period_start && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-text-muted font-manrope">
                                  Current Period
                                </span>
                                <span className="text-sm text-text-primary font-manrope">
                                  {formatDate(enr.current_period_start)} —{" "}
                                  {formatDate(enr.current_period_end)}
                                </span>
                              </div>
                            )}
                            {enr.cancel_at_period_end && (
                              <p className="text-xs text-amber-600 font-manrope mt-1">
                                Cancels at end of current period
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="p-3 sm:p-4 border-t bg-gray-50 flex justify-end">
                  <button
                    onClick={closeViewModal}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-manrope"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
